import {
    Box,
    ButtonBase,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    TextFieldProps,
    Typography,
} from '@deltasierra/react-components-core';
import { ChevronDown, Copy03, CornerLeftUp, SearchSm } from '@deltasierra/react-icons';
import { ShowOn } from '@deltasierra/react-layouts';
import { theme } from '@deltasierra/react-theme';
import { Translations } from '@deltasierra/translations-react';

export type AssetLibraryHeaderProps = {
    currentCollectionTitle: string;
    onChangeSearch: TextFieldProps['onChange'];
    onClickGoToParent?: () => void;
    onClickToggleSelectCollection: (open: boolean) => void;
    search: string;
    tAssetLibrary: Translations<'AssetLibrary'>;
};

export function AssetLibraryHeader({
    currentCollectionTitle,
    onChangeSearch,
    onClickGoToParent,
    onClickToggleSelectCollection,
    search,
    tAssetLibrary,
}: AssetLibraryHeaderProps): JSX.Element {
    return (
        <ShowOn mobile>
            {() => (
                <Stack sx={{ gap: 2, marginX: 2 }}>
                    <Stack direction="row" sx={{ gap: 2 }}>
                        <ButtonBase
                            sx={{
                                alignItems: 'center',
                                border: `1px solid ${theme.palette.text.disabled}`,
                                borderRadius: 1,
                                cursor: 'pointer',
                                display: 'flex',
                                flex: 1,
                                gap: 1,
                                height: 48,
                                minWidth: 0,
                                paddingX: 1.5,
                            }}
                            onClick={() => onClickToggleSelectCollection(true)}
                        >
                            <Box color="primary.main" display="flex">
                                <Copy03 />
                            </Box>
                            <Typography
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                                variant="body1"
                            >
                                {currentCollectionTitle}
                            </Typography>
                            <Box color="text.main" display="flex" sx={{ opacity: 0.5 }}>
                                <ChevronDown />
                            </Box>
                        </ButtonBase>
                        {onClickGoToParent && (
                            <IconButton
                                sx={{
                                    alignItems: 'center',
                                    border: `1px solid ${theme.palette.text.disabled}`,
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flex: '0 0 auto',
                                    height: 48,
                                    justifyContent: 'center',
                                    width: 48,
                                }}
                                onClick={onClickGoToParent}
                            >
                                <Box color="text.main" display="flex" sx={{ opacity: 0.5 }}>
                                    <CornerLeftUp />
                                </Box>
                            </IconButton>
                        )}
                    </Stack>
                    <TextField
                        placeholder={tAssetLibrary('Search assets and folders')}
                        slotProps={{
                            htmlInput: {
                                'aria-label': tAssetLibrary('Search assets and folders'),
                            },
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchSm color={theme.palette.primary.main} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    height: 48,
                                },
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.text.disabled,
                            },
                        }}
                        value={search}
                        onChange={onChangeSearch}
                    />
                </Stack>
            )}
        </ShowOn>
    );
}

AssetLibraryHeader.displayName = 'AssetLibraryHeader';
